import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { ArrowRight, CircleAlert, Plus, Tags } from "lucide-react";
import CountriesSelect from "./ui/countries-dropdowm";
import { Button } from "./ui/button";
import type { AxiosError } from "axios";
import { useNavigate, useOutletContext } from "react-router";
import type { Plan, PromptCluster } from "@/services/api";
import { isPromptCountryLimitReached } from "@/lib/countryLimits";
import type { Brand } from "./AppSidebar";
import { Badge } from "./ui/badge";
import { MultiSelect } from "./ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Spinner } from "./ui/spinner";

const MIN_WORDS = 3;

type AddPromptPayload = {
  country: string;
  text: string;
  isActive: boolean;
  brandId: string;
  clusterId?: string;
  clusterTitle?: string;
};

export const ClusterAdd = ({
  orangeButton = false,
  currentBrand,
  clustersData,
  onPromptAdd,
}: {
  orangeButton?: boolean;
  currentBrand: Brand;
  clustersData: PromptCluster[];
  onPromptAdd: (payload: AddPromptPayload) => Promise<void> | void;
}) => {
  const { t } = useLocale();
  const [step, setStep] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [clusterName, setClusterName] = useState("");
  const [clusterCountry, setClusterCountry] = useState("US");
  const [clusterError, setClusterError] = useState("");
  const [loadingStep, setLoadingStep] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [promptCountry, setPromptCountry] = useState("US");
  const [addPromptError, setAddPromptError] = useState("");
  const navigate = useNavigate();
  const { plan, predefinedCountries } = useOutletContext<{
    plan: Plan;
    predefinedCountries: any;
  }>();
  const isCountryLimited = isPromptCountryLimitReached({
    plan,
    predefinedCountries,
    promptCountry,
    brandCountry: currentBrand?.country,
  });

  const onClusterAdd = () => {
    if (!clusterName.trim().length) {
      return setClusterError("Name can not be empty");
    }
    setLoadingStep(true);
    try {
      setPromptCountry(clusterCountry);
      setStep(2);
    } catch (err: unknown) {
      const error = err as AxiosError<{ message: string }>;
      setClusterError(
        error.response?.data?.message || "Unknown error occurred",
      );
    } finally {
      setLoadingStep(false);
    }
  };

  useEffect(() => {
    if (openDialog) {
      setClusterError("");
    }
  }, [openDialog]);

  const handlePromptAdd = () => {
    if ((!prompt.length || !promptCountry.length) && !isCountryLimited) {
      return setAddPromptError("Prompt and country are required");
    }

    if (isCountryLimited) {
      return setAddPromptError(
        `Expand your reach — upgrade to track prompts across more countries`,
      );
    }

    if (
      prompt
        .trim()
        .split(/\s+/)
        .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
        .filter(Boolean).length < MIN_WORDS
    ) {
      return setAddPromptError(`Please use min. 3 words`);
    }

    const selectedCluster = clustersData?.find(
      (cl) => cl.title === clusterName,
    );
    const newPrompt: AddPromptPayload = {
      country: promptCountry,
      text: prompt,
      isActive: true,
      brandId: currentBrand.brandId || "",
      clusterId: selectedCluster?.id || undefined,
      clusterTitle: selectedCluster?.id ? undefined : clusterName,
    };

    onPromptAdd(newPrompt);
    setPrompt("");
    setClusterName("");
    setStep(1);
    setOpenDialog(false);
  };

  return (
    <Dialog open={openDialog} onOpenChange={setOpenDialog}>
      <Button
        onClick={() => setOpenDialog(true)}
        variant="outline"
        className={`flex items-center justify-center w-full cursor-pointer ${orangeButton && "hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"}`}
      >
        {orangeButton ? (
          "Add cluster"
        ) : (
          <>
            <Plus></Plus> <LocalizedText id="legacy.add.new.5af3iw" />
          </>
        )}
      </Button>
      <DialogContent>
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle className="text-[#0A0A0A] text-lg">
                <LocalizedText id="legacy.add.cluster.1tybzv2" />
              </DialogTitle>
              <DialogDescription className="text-sm text-[#737373]">
                <LocalizedText id="legacy.create.a.cluster.to.group.related.prompts.kfx4c6" />
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <div className="relative">
                <Label
                  className="text-sm font-medium mb-1"
                  htmlFor="clusterName"
                >
                  <LocalizedText id="legacy.name.yjpg1u" />
                </Label>
                <div className="relative">
                  <input
                    value={clusterName}
                    onChange={(e) => {
                      if (e.target.value.length <= 60) {
                        setClusterError("");
                        setClusterName(e.target.value);
                      } else {
                        setClusterError("Cluster name should be max. 60 char.");
                      }
                    }}
                    id="clusterName"
                    type="text"
                    autoComplete="off"
                    className="w-full pr-[60px] px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
                  />
                  <div className="text-sm absolute right-[15px] bottom-[11px] text-[#737373]">
                    {clusterName.length}/60
                  </div>
                </div>
                {clusterError && (
                  <p className="text-red-500 text-sm mt-1">{clusterError}</p>
                )}
              </div>
              <div>
                <Label
                  className="text-sm font-medium mb-1"
                  htmlFor="clisterCountry"
                >
                  <LocalizedText id="legacy.country.1oy8cul" />
                </Label>
                <CountriesSelect
                  isIcon={false}
                  id="clisterCountry"
                  setPromptCountry={setClusterCountry}
                  promptCountry={clusterCountry}
                  isFull={true}
                ></CountriesSelect>
              </div>
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button
                    onClick={() => {
                      setClusterName("");
                      setOpenDialog(false);
                    }}
                    variant="outline"
                    className="cursor-pointer"
                  >
                    <LocalizedText id="legacy.cancel.18x3nqb" />
                  </Button>
                </DialogClose>
                <Button
                  onClick={onClusterAdd}
                  disabled={loadingStep}
                  className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                >
                  {loadingStep && <Spinner></Spinner>}{" "}
                  <LocalizedText id="legacy.add.cluster.mjhrou" />
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle>
                <LocalizedText id="legacy.add.prompt.1vpa268" />
              </DialogTitle>
              <DialogDescription>
                <LocalizedText id="legacy.track.how.ai.models.respond.to.your.brand.10bdt0i" />
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <textarea
                name="prompt"
                autoComplete="off"
                placeholder={t("legacy.enter.a.prompt.13so9y")}
                value={prompt}
                onChange={(e) => {
                  (setAddPromptError(""), setPrompt(e.target.value));
                }}
                className="border rounded-md w-full pt-4 px-4 pb-12"
              />
              <div className="flex gap-2 flex-col">
                <label htmlFor="Country" className="text-sm font-medium">
                  <LocalizedText id="legacy.country.1oy8cul" />
                </label>
                <CountriesSelect
                  isFull={true}
                  isIcon={false}
                  promptCountry={promptCountry}
                  setPromptCountry={setPromptCountry}
                ></CountriesSelect>
              </div>
              {addPromptError && (
                <div className="flex p-4 items-center rounded-md bg-[#DC26261A] gap-2">
                  <div className="p-2 rounded-lg bg-[#DC2626] h-fit">
                    <CircleAlert size={22} className="text-white" />
                  </div>
                  <div className="text-xs">{addPromptError}</div>
                  {isCountryLimited && (
                    <Button
                      onClick={() => navigate(`/dashboard/plans-pricing`)}
                      className="cursor-pointer"
                      variant="outline"
                    >
                      <LocalizedText id="legacy.see.plans.ox5kgm" />{" "}
                      <ArrowRight />
                    </Button>
                  )}
                </div>
              )}
              <div className="flex gap-2 flex-col">
                <label htmlFor="Country" className="text-sm font-medium">
                  <LocalizedText id="legacy.cluster.1tbl9v3" />
                </label>
                <Select value={clusterName}>
                  <SelectTrigger
                    className="text-[#0A0A0A] flex w-full **:data-[slot=select-value]:block **:data-[slot=select-value]:truncate @[767px]/card:hidden"
                    size="sm"
                    aria-label={t("legacy.select.a.value.nv8d47")}
                  >
                    <SelectValue placeholder={clusterName} />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value={clusterName} className="rounded-lg">
                      {clusterName}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 flex-col">
                <label
                  htmlFor="tags"
                  className="text-sm font-medium text-muted-foreground"
                >
                  <LocalizedText id="legacy.tags.yjx284" />{" "}
                  <Badge
                    className="ml-2 text-muted-foreground"
                    variant="outline"
                  >
                    <LocalizedText id="legacy.soon.yjzi9k" />
                  </Badge>{" "}
                </label>
                <MultiSelect
                  className="w-full"
                  options={[]}
                  selected={selectedTags}
                  disabled={true}
                  onChange={(value) => {
                    setAddPromptError("");
                    setSelectedTags(value);
                  }}
                  placeholder={t("legacy.add.a.tag.6ntfyv")}
                >
                  <Tags></Tags>
                </MultiSelect>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button
                  onClick={() => {
                    setPrompt("");
                    setStep(1);
                    setClusterName("");
                  }}
                  variant="outline"
                  className="cursor-pointer"
                >
                  <LocalizedText id="legacy.cancel.18x3nqb" />
                </Button>
              </DialogClose>
              <Button
                type="submit"
                onClick={handlePromptAdd}
                className="cursor-pointer hover:bg-[#ff8138] text-white cursor-pointer bg-[#FF5E00]"
              >
                <LocalizedText id="legacy.add.prompt.g62pgg" />
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
